import { useEffect, useRef, useState, type ReactNode } from 'react'
import { GripVertical, Plus, CheckSquare, Square, Replace, RotateCcw, X } from 'lucide-react'
import type { Block, BlockDimension, BlockSize, BulletListBlock, ListItem, NumberedListBlock, PageInput, SizeUnit, TodoBlock, TodoItem } from './types'
import { getBlockText, setBlockText, makeListItem, makeTodoItem } from './blocks'
import {
  blockStyleCss, resolveBlockStyleFor, headingFontVars,
  animationAttrs, animationClasses, animationVars, hasAnimation, hasHover,
  blockHoverCss, REVEAL_ON_CLASS,
} from './theme'
// The per-block error net and its placeholder are shared with the smart-page
// preview and the deployed runtime — see BlockErrorBoundary for why.
import { BlockErrorBoundary, BlockErrorPlaceholder } from '@twasta/page-runtime/BlockErrorBoundary'
import { useViewport } from './ViewportContext'
import { RepeatFrame } from '@twasta/page-runtime/live/repeatFrame'
import { TimerRenderer } from '@twasta/page-runtime/live/timerBlock'
import EmbedPreview from './EmbedPreview'
import InputRenderer from './InputRenderer'
import NotepadEditor from './NotepadEditor'
import { RichContentRenderer } from '@twasta/page-runtime/RichContentRenderer'
import { blockHeightProp } from '@twasta/page-runtime/ThemedPageShell'
import type { RichContentBlock, CollectionSearchBlock, CommentsBlock } from '@twasta/page-schema'
import { CollectionSearchRenderer, CommentsRenderer } from '@twasta/page-runtime/readerBlocks'
import { CartCheckoutRenderer, MyRecordsRenderer, SavedRecordsRenderer } from '@twasta/page-runtime/commerceBlocks'
import { ReviewsRenderer } from '@twasta/page-runtime/reviewsBlock'
import type { CartCheckoutBlock, MyRecordsBlock, ReviewsBlock, SavedRecordsBlock } from '@twasta/page-schema'
import InlineRichText from './InlineRichText'
import {
  SpacerRenderer, LogoCloudRenderer, ImageRenderer, VideoRenderer, MapRenderer, NavMenuRenderer,
  ChatbotRenderer, AuthFormRenderer, AuthMenuRenderer,
  BentoGridRenderer, MarqueeRenderer, CounterRenderer, BeforeAfterRenderer,
  CarouselRenderer, TeamRenderer, StepsRenderer, ComparisonTableRenderer,
  SocialLinksRenderer, LottieRenderer, CookieConsentRenderer, FloatingButtonRenderer,
  EyebrowLabel, BreadcrumbRenderer, FilterBarRenderer,
  CartBadgeRenderer, CartLinesRenderer, CartSummaryRenderer,
  WishlistButtonRenderer, WishlistBadgeRenderer, WishlistLinesRenderer, CustomHtmlRenderer,
} from './renderers'
import {
  HeroCanvas, CtaBannerCanvas, ColumnsCanvas, ContainerCanvas,
  CardCanvas, CardGridCanvas, FeatureGridCanvas, StatsCanvas, CountdownCanvas,
  TestimonialCanvas, PricingCanvas, FaqCanvas, TabsCanvas,
  AccordionCanvas, GalleryCanvas, ButtonCanvas, ButtonGroupCanvas,
  NewsletterCanvas, ContactFormCanvas, CalloutCanvas, LogoCloudCanvas,
  TableCanvas, TimelineCanvas,
  EntityPickerCanvas, NewCustomerFormCanvas, ProductSelectionCanvas,
  ProductPreviewCanvas, OrderSummaryCanvas, OrderSubmitCanvas, PaymentCheckoutCanvas, PaymentSettingsCanvas,
  FileUploadCanvas,
} from './canvasRenderers'

interface Props {
  block: Block
  index: number
  selected: boolean
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  project: string
  onSelect: () => void
  // Activate a link inside this block (only fired when the block is already
  // selected). The first click on a link in an unselected block selects the
  // block without navigating; a second click activates the link. New-tab
  // links report newTab=true. Optional — when absent, links never navigate
  // from the canvas (they only select).
  onActivateLink?: (href: string, newTab: boolean) => void
  onChange: (next: Block) => void
  onTextKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, block: Block) => void
  onInsertAfter: () => void
  onTransform: (anchor: { left: number; top: number; bottom: number }) => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
  // When false, the drag-to-reorder handle is hidden. Reordering is only
  // wired up for the main section today; header/footer blocks set this to
  // false to avoid a non-functional affordance.
  canDrag?: boolean
  onInputValueChange: (name: string, value: string) => void
  // Set a page-input value to ANY structured value (string, number,
  // object, array, undefined). Used by the embed live preview to push
  // row-selection state into the designer's variable map so downstream
  // blocks reactively re-render. Optional — when absent, embed
  // selection is a no-op for variable binding (highlight still works).
  onSetInputValue?: (name: string, value: unknown) => void
  showDropIndicator: 'before' | 'after' | null
}

function coerceToString(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  // Objects/arrays (json variables) — render as compact JSON in
  // input controls so the user can still see something legible.
  try { return JSON.stringify(v) } catch { return '' }
}

// Resolve `{{name}}` / `{{name.path}}` placeholders against the page-level
// input values map. Mirrors PageVarsService.resolve in the Angular runtime
// so the canvas preview of heading/paragraph/quote/code blocks shows the
// same text the runtime renders. Unresolved keys collapse to empty string
// (matches runtime), so a heading bound to a not-yet-set variable simply
// loses the placeholder rather than showing literal `{{...}}`.
function resolveVarText(text: string, vals: Record<string, unknown>): string {
  if (!text || !text.includes('{{')) return text
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, expr: string) => {
    const [head, ...rest] = expr.split('.')
    let v: unknown = vals[head]
    for (const part of rest) {
      if (v && typeof v === 'object') v = (v as Record<string, unknown>)[part]
      else return ''
    }
    if (v === undefined || v === null) return ''
    return String(v)
  })
}

const PLACEHOLDERS: Record<string, string> = {
  heading: 'Heading',
  paragraph: "Type '/' for commands",
  bullet_list: 'List item',
  numbered_list: 'List item',
  todo: 'To-do',
  quote: 'Quote',
  code: 'Code',
}

export default function BlockNode({
  block,
  index: _index,
  selected,
  inputs,
  inputValues,
  project,
  onSelect,
  onActivateLink,
  onChange,
  onTextKeyDown,
  onInsertAfter,
  onTransform,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  canDrag = true,
  onInputValueChange,
  onSetInputValue,
  showDropIndicator,
}: Props) {
  const textRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const viewport = useViewport()
  const currentText = getBlockText(block)
  const [isDragging, setIsDragging] = useState(false)
  // Toggles between showing raw `{{var}}` template text (while the user
  // is actively editing this contenteditable) and the resolved value
  // (when not focused). Without this, the canvas would always show
  // template source, making it impossible to verify variable-driven
  // bindings from row selection without opening the runtime preview.
  const [textFocused, setTextFocused] = useState(false)

  // Sync external block.text → DOM only when it differs (avoids caret reset on every keystroke).
  // Resolves `{{var}}` against inputValues when the user is not editing this block, so a row
  // selection that sets `selected_project` shows up in a heading bound to
  // "Tasks for {{selected_project.proj_name}}" immediately on the canvas.
  useEffect(() => {
    if (currentText === null) return
    const el = textRef.current
    if (!el) return
    const display = textFocused
      ? currentText
      : resolveVarText(currentText, inputValues)
    if (el.textContent !== display) {
      el.textContent = display
    }
  }, [currentText, textFocused, inputValues])

  const renderTextEditable = (style: React.CSSProperties = {}) => (
    <div
      ref={textRef}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={PLACEHOLDERS[block.type] || ''}
      onInput={(e) => {
        const next = setBlockText(block, (e.currentTarget as HTMLDivElement).innerText)
        if (next !== block) onChange(next)
      }}
      onFocus={() => { setTextFocused(true); onSelect() }}
      onBlur={() => setTextFocused(false)}
      onKeyDown={(e) => onTextKeyDown(e, block)}
      style={{
        outline: 'none',
        minHeight: '1.4em',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        ...style,
      }}
    />
  )

  const blockBody = (() => {
    switch (block.type) {
      case 'heading': {
        // Size/weight/line-height/tracking/family all come from the theme type
        // scale (`--pg-h1/2/3` by level) so headings honor baseSize/scaleRatio
        // and match every other surface. Was hardcoded 1.6/1.3/1.05rem.
        // Default to the new heading token so headings get the explicit
        // (typically slightly darker) color the preset declares for
        // h1/h2/h3 hierarchy. Falls back to body text when the theme
        // hasn't supplied a separate heading color, then to a literal so
        // legacy un-themed pages still render legibly.
        const color = block.style?.textColor || 'var(--pg-heading, var(--pg-text, #0f172a))'
        const heading = (
          <InlineRichText
            block={block}
            onChange={onChange}
            onSelect={onSelect}
            onKeyDown={onTextKeyDown}
            inputValues={inputValues}
            placeholder={PLACEHOLDERS.heading}
            style={{ ...headingFontVars(block.level), color }}
          />
        )
        if (!block.eyebrow) return heading
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <EyebrowLabel text={block.eyebrow} />{heading}
          </div>
        )
      }
      case 'paragraph': {
        const color = block.style?.textColor || 'var(--pg-text, #0f172a)'
        return (
          <InlineRichText
            block={block}
            onChange={onChange}
            onSelect={onSelect}
            onKeyDown={onTextKeyDown}
            inputValues={inputValues}
            placeholder={PLACEHOLDERS.paragraph}
            style={{ fontSize: '1.0125rem', lineHeight: 1.5, color }}
          />
        )
      }
      case 'bullet_list':
        return <ListBlockEditor block={block} kind="bullet" onChange={onChange} onSelect={onSelect} />
      case 'numbered_list':
        return <ListBlockEditor block={block} kind="numbered" onChange={onChange} onSelect={onSelect} />
      case 'todo':
        return (
          <TodoListEditor
            block={block}
            onChange={onChange}
            onSelect={onSelect}
          />
        )
      case 'quote':
        return (
          <div style={{
            borderLeft: '3px solid var(--pg-primary)',
            paddingLeft: 12,
            fontStyle: 'italic',
          }}>
            <InlineRichText
              block={block}
              onChange={onChange}
              onSelect={onSelect}
              onKeyDown={onTextKeyDown}
              inputValues={inputValues}
              placeholder={PLACEHOLDERS.quote}
              style={{ fontSize: '1.0125rem', lineHeight: 1.5, color: 'var(--pg-text, #1e293b)' }}
            />
          </div>
        )
      case 'code':
        return (
          <div style={{
            background: 'var(--pg-surface)',
            border: '1px solid var(--pg-border)',
            borderRadius: 6,
            padding: 10,
            fontFamily: 'monospace',
            fontSize: '0.9125rem',
            position: 'relative',
          }}>
            {block.language && (
              <div style={{
                position: 'absolute', right: 8, top: 4,
                fontSize: '0.8125rem', color: 'var(--pg-text-secondary)',
                textTransform: 'uppercase',
              }}>{block.language}</div>
            )}
            {renderTextEditable({ whiteSpace: 'pre', color: 'var(--pg-text, #0f172a)' })}
          </div>
        )
      case 'divider':
        return (
          <div style={{ padding: '6px 0' }}>
            <hr style={{ border: 'none', borderTop: '1px solid var(--pg-border)', margin: 0 }} />
          </div>
        )
      case 'image':
        return <ImageRenderer block={block} />
      case 'video':
        return <VideoRenderer block={block} />
      case 'map':
        return <MapRenderer block={block} />
      case 'gallery':
        return <GalleryCanvas block={block} onChange={onChange} />
      case 'logo_cloud':
        return <LogoCloudCanvas block={block} onChange={onChange} />
      case 'hero':
        return <HeroCanvas block={block} onChange={onChange} />
      case 'cta_banner':
        return <CtaBannerCanvas block={block} onChange={onChange} />
      case 'spacer':
        return <SpacerRenderer block={block} />
      case 'columns':
        return <ColumnsCanvas block={block} onChange={onChange} />
      case 'container':
        return <ContainerCanvas block={block} onChange={onChange} />
      case 'card':
        return <CardCanvas block={block} onChange={onChange} />
      case 'breadcrumb':
        return <BreadcrumbRenderer block={block} />
      case 'filter_bar':
        return <FilterBarRenderer block={block} />
      case 'cart_badge':
        return <CartBadgeRenderer block={block} />
      case 'cart_lines':
        return <CartLinesRenderer block={block} />
      case 'cart_summary':
        return <CartSummaryRenderer block={block} />
      case 'wishlist_button':
        return <WishlistButtonRenderer block={block} />
      case 'wishlist_badge':
        return <WishlistBadgeRenderer block={block} />
      case 'wishlist_lines':
        return <WishlistLinesRenderer block={block} />
      case 'card_grid':
        return <CardGridCanvas block={block} onChange={onChange} />
      case 'feature_grid':
        return <FeatureGridCanvas block={block} onChange={onChange} />
      case 'stats':
        return <StatsCanvas block={block} onChange={onChange} />
      case 'countdown':
        return <CountdownCanvas block={block} onChange={onChange} />
      case 'timer':
        // The canvas has no row: a token start shows a sample age so the
        // author sees the size, colour and format they are choosing.
        return (
          <TimerRenderer
            live={false}
            block={(block.from || '').includes('{{')
              ? { ...block, from: new Date(Date.now() - 245_000).toISOString(), until: '', warnAfter: '', dangerAfter: '' }
              : block}
          />
        )
      case 'testimonial':
        return <TestimonialCanvas block={block} onChange={onChange} />
      case 'pricing':
        return <PricingCanvas block={block} onChange={onChange} />
      case 'faq':
        return <FaqCanvas block={block} onChange={onChange} />
      case 'tabs':
        return <TabsCanvas block={block} onChange={onChange} />
      case 'accordion':
        return <AccordionCanvas block={block} onChange={onChange} />
      case 'callout':
        return <CalloutCanvas block={block} onChange={onChange} />
      case 'timeline':
        return <TimelineCanvas block={block} onChange={onChange} />
      case 'button':
        return <ButtonCanvas block={block} onChange={onChange} />
      case 'button_group':
        return <ButtonGroupCanvas block={block} onChange={onChange} />
      case 'newsletter':
        return <NewsletterCanvas block={block} onChange={onChange} />
      case 'contact_form':
        return <ContactFormCanvas block={block} onChange={onChange} />
      case 'input_group': {
        const used = block.inputs
          .map((name) => inputs.find((i) => i.name === name))
          .filter((i): i is PageInput => Boolean(i))
        if (used.length === 0) {
          return <div style={emptyBlockStyle}>⌨ Pick page inputs to render in the property panel</div>
        }
        return (
          <div style={{
            display: 'flex',
            flexDirection: block.layout === 'stacked' ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            {used.map((inp) => (
              <InputRenderer
                key={inp.name}
                input={inp}
                value={coerceToString(inputValues[inp.name])}
                onChange={(v) => onInputValueChange(inp.name, v)}
                layout={block.layout}
              />
            ))}
          </div>
        )
      }
      case 'embed': {
        // `minWidth: 0` lets a wide embed (a transaction grid once its data
        // loads) shrink instead of widening the canvas column; `maxWidth: 100%`
        // caps it — the embed's table surface scrolls internally. No `height`:
        // the block stays content-tall; an explicit height goes straight to the
        // visual via `bodyHeight` (a stretchy height:100% here left blank space).
        const h = block.size?.height
        const bodyHeight = h && typeof h.value === 'number' ? `${h.value}${h.unit}` : undefined
        return (
          <div style={{ minWidth: 0, maxWidth: '100%' }}>
            <EmbedPreview
              project={project}
              embed={block.embed}
              inputs={inputs}
              inputValues={inputValues}
              onSetInput={onSetInputValue}
              bodyHeight={bodyHeight}
            />
          </div>
        )
      }
      case 'file_upload':
        return <FileUploadCanvas block={block} />
      case 'form':
        return (
          <div style={{ padding: 16, border: '1px dashed var(--pg-border)', borderRadius: 8, background: 'var(--pg-surface)' }}>
            {block.heading && <div style={{ fontWeight: 600, marginBottom: 4 }}>{block.heading}</div>}
            {block.subheading && <div style={{ color: 'var(--pg-text-secondary)', fontSize: 14, marginBottom: 12 }}>{block.subheading}</div>}
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: block.layout === 'grid' ? '1fr 1fr' : '1fr' }}>
              {block.fields.map(f => (
                <div key={f.id} style={{ display: 'grid', gap: 4 }}>
                  <label style={{ fontSize: 13, color: 'var(--pg-text-secondary)' }}>{f.label}{f.required && ' *'}</label>
                  {f.type === 'textarea' ? (
                    <textarea placeholder={f.placeholder} rows={3} style={{ padding: '6px 8px', fontSize: 14, border: '1px solid var(--pg-border)', borderRadius: 4, background: 'var(--pg-bg)', resize: 'vertical' }} />
                  ) : f.type === 'select' ? (
                    <select style={{ padding: '6px 8px', fontSize: 14, border: '1px solid var(--pg-border)', borderRadius: 4, background: 'var(--pg-bg)' }}>
                      {(f.options ?? []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} placeholder={f.placeholder} style={{ padding: '6px 8px', fontSize: 14, border: '1px solid var(--pg-border)', borderRadius: 4, background: 'var(--pg-bg)' }} />
                  )}
                </div>
              ))}
            </div>
            <button type="button" disabled style={{ marginTop: 12, padding: '8px 14px', fontSize: 14, border: 'none', borderRadius: 4, background: 'var(--pg-primary)', color: 'var(--pg-on-primary, #fff)', cursor: 'not-allowed' }}>{block.submitLabel}</button>
          </div>
        )
      // Custom components RUN on the canvas — the same sandboxed frame as
      // every published surface (an infographic drawn by script, a Claude
      // Design component), so what the author sees is what visitors get.
      // `inert`: clicks select the block instead of reaching the frame.
      case 'custom_code':
      case 'custom_html':
        return <CustomHtmlRenderer block={block} inert />
      case 'nav_menu':
        return <NavMenuRenderer block={block} />
      case 'variable': {
        const v: any = block
        const valRaw = v.name ? inputValues[v.name] : ''
        const isEmpty = valRaw === '' || valRaw == null
        const isJson = v.format === 'json'
        const sizePx = v.textSize === 'sm' ? 14 : v.textSize === 'lg' ? 22 : v.textSize === 'xl' ? 32 : 18
        if (isJson) {
          const jsonText = isEmpty
            ? (v.emptyText ?? '—')
            : typeof valRaw === 'object'
              ? (() => { try { return JSON.stringify(valRaw, null, 2) } catch { return String(valRaw) } })()
              : formatValue(coerceToString(valRaw), 'json')
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {v.label && <span style={{ fontSize: 13, color: 'var(--pg-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{v.label}</span>}
              <pre style={{
                margin: 0, padding: 10, maxHeight: 320, overflow: 'auto',
                border: '1px solid var(--pg-border)', borderRadius: 8, background: 'var(--pg-surface)',
                fontSize: '0.8625rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>{jsonText}</pre>
              {!v.name && <span style={{ fontSize: 13, color: '#dc2626' }}>Pick a variable in the property panel.</span>}
            </div>
          )
        }
        const display = isEmpty
          ? (v.emptyText ?? '—')
          : `${v.prefix ?? ''}${formatValue(coerceToString(valRaw), v.format)}${v.suffix ?? ''}`
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: v.align === 'center' ? 'center' : v.align === 'right' ? 'flex-end' : 'flex-start' }}>
            {v.label && <span style={{ fontSize: 13, color: 'var(--pg-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{v.label}</span>}
            <span style={{ fontSize: sizePx, fontWeight: 600 }}>{display}</span>
            {!v.name && <span style={{ fontSize: 13, color: '#dc2626' }}>Pick a variable in the property panel.</span>}
          </div>
        )
      }
      case 'collection_search':
        return <CollectionSearchRenderer block={block as CollectionSearchBlock} design />
      case 'comments':
        return <CommentsRenderer block={block as CommentsBlock} design />
      // Storefront commerce blocks: sample content on the canvas.
      case 'cart_checkout':
        return <CartCheckoutRenderer block={block as CartCheckoutBlock} design />
      case 'my_records':
        return <MyRecordsRenderer block={block as MyRecordsBlock} design />
      case 'saved_records':
        return <SavedRecordsRenderer block={block as SavedRecordsBlock} design />
      case 'reviews':
        return <ReviewsRenderer block={block as ReviewsBlock} design />
      case 'rich_content': {
        const rc = block as RichContentBlock
        return (
          <RichContentRenderer
            block={rc}
            vars={{}}
            placeholder={
              <div style={{
                padding: '16px 18px', border: '1px dashed var(--pg-border)', borderRadius: 8,
                color: 'var(--pg-text-secondary, var(--pg-text))', fontSize: 14, lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>📰 Rich content</div>
                {rc.source?.trim()
                  ? <>Shows <code>{rc.source}</code> — the record’s formatted text (headings, images, tables) appears here in Preview and on the published page.</>
                  : <span style={{ color: '#dc2626' }}>Choose the Rich Text column to show in the property panel.</span>}
              </div>
            }
          />
        )
      }
      case 'notepad':
        return (
          <NotepadEditor
            block={block}
            onChange={onChange}
            onSelect={onSelect}
          />
        )
      case 'table':
        return <TableCanvas block={block} onChange={onChange} />
      case 'entity_picker':
        return <EntityPickerCanvas block={block} />
      case 'new_customer_form':
        return <NewCustomerFormCanvas block={block} />
      case 'product_selection':
        return <ProductSelectionCanvas block={block} />
      case 'product_preview':
        return <ProductPreviewCanvas block={block} onChange={onChange} />
      case 'order_summary':
        return <OrderSummaryCanvas block={block} />
      case 'order_submit':
        return <OrderSubmitCanvas block={block} />
      case 'payment_checkout':
        return <PaymentCheckoutCanvas block={block} />
      case 'payment_settings':
        return <PaymentSettingsCanvas block={block} />
      case 'chatbot':
        // Faithful canvas preview using the runtime renderer; no `onSend`
        // adapter is wired in the designer, so sending shows an inert notice.
        return <ChatbotRenderer block={block} />
      case 'auth_form':
        // Visual-only preview (no `onSubmit` adapter in the designer).
        return <AuthFormRenderer block={block} />
      case 'auth_menu':
        // Signed-out preview (no AuthMenuContext is provided in the designer).
        return <AuthMenuRenderer block={block} />

      // ── Marketing sections (Tier 6) ──
      // These reuse the SHARED renderers verbatim rather than getting
      // canvas-specific twins: they are presentational, so what the author
      // sees here is byte-identical to what publishes. Their content is
      // edited from the property panel (there is no meaningful in-place text
      // editing for a comparison matrix or a bento tile).
      case 'bento_grid':       return <BentoGridRenderer block={block} />
      case 'marquee':          return <MarqueeRenderer block={block} />
      case 'counter':          return <CounterRenderer block={block} />
      case 'before_after':     return <BeforeAfterRenderer block={block} />
      case 'carousel':         return <CarouselRenderer block={block} />
      case 'team':             return <TeamRenderer block={block} />
      case 'steps':            return <StepsRenderer block={block} />
      case 'comparison_table': return <ComparisonTableRenderer block={block} />
      case 'social_links':     return <SocialLinksRenderer block={block} />
      case 'lottie':           return <LottieRenderer block={block} />
      case 'cookie_consent':
        // The real banner is `position: fixed` and hidden until the consent
        // script reveals it — which would make it invisible on the canvas and
        // float over the whole editor if it weren't. Show it inline instead so
        // the author can see and edit the copy.
        return (
          <div style={{ position: 'relative' }}>
            <div style={{ fontSize: 13, color: 'var(--pg-text-secondary)', marginBottom: 6 }}>
              Cookie banner — shown fixed to the {block.position || 'bottom'} at runtime, once, until answered.
            </div>
            <div style={{ position: 'static', display: 'flex' }} className="pg-consent" data-open="">
              <CookieConsentRenderer block={block} />
            </div>
          </div>
        )
      case 'floating_button':
        // Fixed to the viewport at runtime — on the canvas it would float over
        // the editor, so show it inline with a note of where it will sit.
        return (
          <div>
            <div style={{ fontSize: 13, color: 'var(--pg-text-secondary)', marginBottom: 6 }}>
              Floating button — pinned to the {(block.position || 'bottom-right').replace('-', ' ')} of the screen at runtime
              {block.vertical ? ', turned on its side' : ''}.
            </div>
            <FloatingButtonRenderer block={block} inline />
          </div>
        )
      default:
        // Unknown block type — usually drift in generator output. Render a
        // visible placeholder so the author can locate and replace it.
        return (
          <BlockErrorPlaceholder
            title={`Unknown block type: ${(block as { type?: string }).type ?? '?'}`}
            hint="This block type isn't supported by the designer. Delete it or replace it with a supported block."
          />
        )
    }
  })()
  const safeBlockBody = (
    <BlockErrorBoundary blockId={block.id} blockType={block.type}>
      {blockBody}
    </BlockErrorBoundary>
  )

  // ── A repeat, on the canvas ──
  // The canvas has no live rows, so a data-bound block used to draw as a
  // single item — which tells the author nothing about the layout they are
  // actually authoring, and hides the difference between a flat stack and a
  // grid entirely. Draw `designCount` copies of the body (default 3, the
  // schema's own default) in the real layout wrapper, with a quiet note that
  // they stand in for rows. One selection, one block: the copies are
  // scenery, not children.
  const repeat = block.repeat
  const repeatMode = repeat?.layout?.mode || 'flow'
  const bodyWithRepeat = repeat?.source ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase',
        color: 'var(--pg-text-secondary)', opacity: 0.85,
      }}>
        Repeats over “{repeat.source}”
        {repeatMode === 'grid'
          ? ` · ${repeat.layout?.columns ?? 3}-column grid`
          : repeatMode === 'board'
            ? ` · board by ${repeat.layout?.groupBy || '(no column)'}`
            : repeatMode === 'positioned'
              ? ' · positioned plane'
              : ' · stacked'}
        {repeat.select ? ` · ${repeat.select.mode === 'single' ? 'single' : 'multi'}-select → ${repeat.select.var}` : ''}
        {' '}· placeholder rows
      </span>
      {/* The SAME arrangement every live surface uses (RepeatFrame), fed
          placeholder rows, so lanes / a plane look as they will run. */}
      <RepeatFrame
        blockId={block.id}
        repeat={repeat}
        rows={placeholderRows(repeat)}
        interactive={false}
        renderCopy={(_row, i) => (
          // A grid cell is a flex column so a card copy can fill it —
          // the same equal-height rows the published grid draws.
          <div style={{
            ...(i === 0 ? {} : { opacity: 0.55 }),
            ...(repeatMode !== 'flow' ? { display: 'flex', flexDirection: 'column', height: '100%' } : { marginBottom: 12 }),
          }}>
            {safeBlockBody}
          </div>
        )}
      />
    </div>
  ) : safeBlockBody

  // ── Sizing: apply explicit width/height (px or %) when set ──
  // Layered styles: size → block.style overrides (background, padding,
  // margin, border, radius, shadow, text color) → drawing the block body.
  // Style.padding overrides the default 8px left padding when set.
  const size = block.size
  // Resolve the style for the device being previewed so per-breakpoint
  // overrides are visible on the (width-constrained) canvas. Falls back to
  // the base style on desktop / when the block has no responsive overrides.
  const resolvedStyle = resolveBlockStyleFor(block, viewport)
  // `bleed` sizes a block to the browser window (100vw), which on the canvas
  // is not the page: the block would slide off the canvas. It spans the
  // canvas width here and the full window everywhere the page is served.
  const styleCss = blockStyleCss(resolvedStyle?.bleed ? { ...resolvedStyle, bleed: false } : resolvedStyle, block.type)
  const hasStylePadding = !!resolvedStyle?.padding
  const sizedWrapperStyle: React.CSSProperties = {
    position: 'relative',
    paddingLeft: hasStylePadding ? undefined : 8,
    width: dimToCss(size?.width) ?? '100%',
    maxWidth: '100%',
    // Flowing blocks (text, menus, columns) grow past it — same rule as StyledBlock.
    [blockHeightProp(block.type)]: dimToCss(size?.height),
    // Keep overflow visible so a fixed-height block (e.g. a header bar) doesn't
    // clip absolutely-positioned descendants (nav dropdowns) or add a
    // scrollbar — parity with the runtime/preview/SSR size wrappers.
    boxSizing: 'border-box',
    ...styleCss,
    ...animationVars(block.animation),
  }

  // ── Motion on the canvas ──
  // Scroll/enter animations play once and then sit in their final state, so
  // an author would see the effect at most once and could never tune the
  // timing. The canvas therefore does NOT arm the reveal observer; it holds
  // the animation in its finished state (`.pg-in` present) and gives the
  // selected block a replay control that removes and re-adds the class on
  // the next frame, restarting the animation on demand.
  const animCls = animationClasses(block.animation)
  const wrapperClasses = [
    block.style?.className,
    ...animCls,
    hasAnimation(block.animation) ? REVEAL_ON_CLASS : '',
    hasHover(resolvedStyle) ? `pg-b-${block.id}` : '',
  ].filter(Boolean).join(' ') || undefined

  const replay = () => {
    const el = wrapperRef.current
    if (!el) return
    // Force a reflow between the remove and the re-add — without it the
    // browser coalesces both into "no change" and nothing replays.
    el.classList.remove(REVEAL_ON_CLASS)
    el.classList.add('pg-anim-replay')
    void el.offsetWidth
    el.classList.remove('pg-anim-replay')
    requestAnimationFrame(() => el.classList.add(REVEAL_ON_CLASS))
    for (const child of Array.from(el.children)) {
      ;(child as HTMLElement).style.animation = ''
    }
  }

  // Drag-to-resize: width on right edge, height on bottom edge, both on
  // the bottom-right corner. Updates onChange continuously while dragging.
  const startResize = (
    e: React.MouseEvent,
    axis: 'x' | 'y' | 'xy',
    container: HTMLElement | null,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (!container) return
    const startX = e.clientX
    const startY = e.clientY
    const rect = container.getBoundingClientRect()
    const startWidth = rect.width
    const startHeight = rect.height
    // Resolve unit defaults from current block.size (or 'px' if absent)
    const wUnit: SizeUnit = block.size?.width?.unit || 'px'
    const hUnit: SizeUnit = block.size?.height?.unit || 'px'
    const parent = container.parentElement
    const parentWidth = parent ? parent.getBoundingClientRect().width : startWidth
    const parentHeight = parent ? parent.getBoundingClientRect().height : startHeight

    const toUnitWidth = (px: number): BlockDimension =>
      wUnit === '%'
        ? { value: clamp(Math.round((px / Math.max(1, parentWidth)) * 1000) / 10, 5, 100), unit: '%' }
        : { value: Math.max(40, Math.round(px)), unit: 'px' }
    const toUnitHeight = (px: number): BlockDimension =>
      hUnit === '%'
        ? { value: clamp(Math.round((px / Math.max(1, parentHeight)) * 1000) / 10, 5, 200), unit: '%' }
        : { value: Math.max(20, Math.round(px)), unit: 'px' }

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      const next: BlockSize = { ...(block.size || {}) }
      if (axis === 'x' || axis === 'xy') {
        next.width = toUnitWidth(startWidth + dx)
      }
      if (axis === 'y' || axis === 'xy') {
        next.height = toUnitHeight(startHeight + dy)
      }
      onChange({ ...block, size: next })
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor =
      axis === 'x' ? 'ew-resize' : axis === 'y' ? 'ns-resize' : 'nwse-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // Intercept clicks on real links rendered inside the block (nav menus,
  // buttons-as-links, linked text, etc.) so the canvas stays an editing
  // surface. First click on a link in an UNSELECTED block: select the block,
  // don't navigate. Once the block is selected, a click on the link
  // activates it (the page owner confirms leaving the designer). Runs in the
  // capture phase so we can cancel the browser's default navigation before
  // it happens; we never stopPropagation, so the wrapper's onClick still
  // selects the block.
  const handleClickCapture = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a') as HTMLAnchorElement | null
    if (!anchor) return
    // New-tab / download links keep the designer open — let them behave
    // normally (no prompt, no select-only interception).
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return
    const href = anchor.getAttribute('href') || ''
    // Non-navigating anchors (placeholders / JS handlers): swallow so nothing
    // happens, but let the block select via the bubbling onClick.
    if (!href || href === '#' || href.startsWith('javascript:')) {
      e.preventDefault()
      return
    }
    if (!selected) {
      // Select-only: cancel navigation; the bubbling onClick selects.
      e.preventDefault()
      return
    }
    // Already selected → activate the link (confirm + navigate upstream).
    e.preventDefault()
    onActivateLink?.(anchor.href, false)
  }

  return (
    <div
      onClick={onSelect}
      onClickCapture={handleClickCapture}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={() => {
        setIsDragging(false)
        onDragEnd()
      }}
      style={{
        // No vertical padding here — the visible gap between blocks is
        // owned entirely by the wrap's `--pg-block-gap`-driven padding so
        // page/section overrides resolve to the exact value the user sets
        // (any padding here would silently add to the gap).
        position: 'relative',
        background: selected ? 'rgba(76,141,255,0.04)' : 'transparent',
        borderRadius: 4,
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity 0.12s',
      }}
    >
      {/* Drop indicator */}
      {showDropIndicator === 'before' && (
        <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 2, background: 'var(--pg-accent)' }} />
      )}
      {showDropIndicator === 'after' && (
        <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 2, background: 'var(--pg-accent)' }} />
      )}

      {/* Hover handles — anchored to the right edge of the gutter so the
          drag handle (rightmost button) lands a few px to the LEFT of the
          block instead of bleeding into its content. Combined width of
          the three buttons is ≈74px; the canvas enforces ≥88px of
          horizontal padding so the whole group has room. */}
      <div
        className="block-handles"
        style={{
          position: 'absolute',
          right: 'calc(100% + 6px)',
          top: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          opacity: 0,
          transition: 'opacity 0.15s',
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onInsertAfter() }}
          title="Add block below"
          style={handleBtnStyle}
        >
          <Plus size={14} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation()
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
            onTransform({ left: rect.left, top: rect.top, bottom: rect.bottom })
          }}
          title="Change block type"
          style={handleBtnStyle}
        >
          <Replace size={13} />
        </button>
        {canDrag && (
          <button
            draggable
            onDragStart={(e) => {
              setIsDragging(true)
              onDragStart(e)
            }}
            title="Drag to reorder"
            aria-label="Drag handle — drag to reorder this block"
            style={dragHandleStyle(isDragging)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <GripVertical size={13} />
          </button>
        )}
      </div>

      {/* Hover-state rules can't be expressed inline, so a block carrying
          them emits its scoped rule here — the canvas shows the real hover
          on the real element rather than a swatch. */}
      {hasHover(resolvedStyle) && (
        <style dangerouslySetInnerHTML={{ __html: blockHoverCss(`pg-b-${block.id}`, resolvedStyle, block.type) }} />
      )}
      <div
        ref={wrapperRef}
        id={block.anchorId || undefined}
        className={wrapperClasses}
        style={sizedWrapperStyle}
        {...animationAttrs(block.animation)}
        // The canvas never arms the reveal observer (see `replay` above), so
        // strip the hook the observer looks for. Without this the block would
        // be hidden on the canvas the moment any surface armed the observer.
        data-pg-reveal={undefined}
      >
        {bodyWithRepeat}

        {/* Motion replay — without it Tier-1 animation would ship as a
            feature authors still couldn't tune. */}
        {selected && hasAnimation(block.animation) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); replay() }}
            title="Replay animation"
            aria-label="Replay this block's animation"
            style={replayBtnStyle}
          >
            <RotateCcw size={12} />
            <span>Replay</span>
          </button>
        )}

        {/* Resize handles — visible on hover/selection */}
        {selected && (
          <>
            {/* Right edge — width */}
            <div
              onMouseDown={(e) => startResize(e, 'x', wrapperRef.current)}
              title={`Drag to resize width (${block.size?.width?.unit || 'px'})`}
              style={{
                position: 'absolute', top: 0, bottom: 0, right: -3,
                width: 6, cursor: 'ew-resize',
                background: 'transparent',
              }}
            >
              <div style={resizeEdgeIndicator('vertical')} />
            </div>
            {/* Bottom edge — height */}
            <div
              onMouseDown={(e) => startResize(e, 'y', wrapperRef.current)}
              title={`Drag to resize height (${block.size?.height?.unit || 'px'})`}
              style={{
                position: 'absolute', left: 0, right: 0, bottom: -3,
                height: 6, cursor: 'ns-resize',
                background: 'transparent',
              }}
            >
              <div style={resizeEdgeIndicator('horizontal')} />
            </div>
            {/* Bottom-right corner — both */}
            <div
              onMouseDown={(e) => startResize(e, 'xy', wrapperRef.current)}
              title="Drag to resize"
              style={{
                position: 'absolute', right: -4, bottom: -4,
                width: 12, height: 12, cursor: 'nwse-resize',
                background: 'var(--pg-accent)',
                borderRadius: 2,
                boxShadow: '0 0 0 2px var(--pg-bg)',
                opacity: 0.85,
              }}
            />
          </>
        )}
      </div>
    </div>
  )
}

// Convert BlockDimension → CSS value string (or undefined when not set).
function dimToCss(d: BlockDimension | undefined): string | undefined {
  if (!d) return undefined
  if (typeof d.value !== 'number' || !isFinite(d.value)) return undefined
  return `${d.value}${d.unit}`
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n))
}

const resizeEdgeIndicator = (axis: 'vertical' | 'horizontal'): React.CSSProperties => ({
  position: 'absolute',
  ...(axis === 'vertical'
    ? { top: '40%', bottom: '40%', left: 1, right: 1 }
    : { left: '40%', right: '40%', top: 1, bottom: 1 }),
  background: 'var(--pg-accent)',
  borderRadius: 1,
  opacity: 0.6,
})

// Replay control for the selected block's entrance animation. Anchored to
// the block's top-right so it never collides with the drag/insert handles on
// the left gutter or the resize handles on the right/bottom edges.
const replayBtnStyle: React.CSSProperties = {
  position: 'absolute', top: 4, right: 4, zIndex: 5,
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px', fontSize: 13, fontWeight: 600,
  background: 'var(--pg-surface)', color: 'var(--pg-text-secondary)',
  border: '1px solid var(--pg-border)', borderRadius: 999, cursor: 'pointer',
}

const handleBtnStyle: React.CSSProperties = {
  width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--pg-surface)',
  border: '1px solid var(--pg-border)',
  borderRadius: 4, cursor: 'pointer',
  color: 'var(--pg-text-secondary)',
}

// Drag-handle styling: matches the sibling + / transform buttons so the
// three handles read as one toolbar. The "grabbing" cursor + faint accent
// ring is the only visual change while a drag is in flight.
function dragHandleStyle(dragging: boolean): React.CSSProperties {
  return {
    ...handleBtnStyle,
    cursor: dragging ? 'grabbing' : 'grab',
    boxShadow: dragging ? '0 0 0 2px rgba(76,141,255,0.25)' : undefined,
    transition: 'box-shadow 0.12s',
  }
}

const emptyBlockStyle: React.CSSProperties = {
  padding: '12px 14px',
  background: 'var(--pg-bg)',
  border: '1px dashed var(--pg-border)',
  borderRadius: 6,
  color: 'var(--pg-text-secondary)',
  fontSize: '0.9125rem',
}

// Multi-item to-do list editor — renders all items inside one block.
// Enter splits the current item (creating a new item below), Backspace at
// the start of an empty item removes it (and merges focus into the previous
// item). All edits are batched into a single TodoBlock onChange so undo
// captures the list as one block.
interface TodoListEditorProps {
  block: TodoBlock
  onChange: (next: TodoBlock) => void
  onSelect: () => void
}

function TodoListEditor({ block, onChange, onSelect }: TodoListEditorProps) {
  // After a structural mutation (add/remove item) we need to move the caret
  // to the new item's editor on the next paint. The id of the item that
  // should receive focus is stashed here.
  const focusItemRef = useRef<{ id: string; caret: 'start' | 'end' } | null>(null)
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    const target = focusItemRef.current
    if (!target) return
    const el = itemRefs.current.get(target.id)
    if (el) {
      el.focus()
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(target.caret === 'start')
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(range)
    }
    focusItemRef.current = null
  })

  const items = block.items.length > 0 ? block.items : [makeTodoItem()]

  const setItems = (next: TodoItem[]) => onChange({ ...block, items: next })

  const updateItem = (id: string, patch: Partial<TodoItem>) => {
    setItems(items.map(it => (it.id === id ? { ...it, ...patch } : it)))
  }

  const insertAfter = (id: string) => {
    const idx = items.findIndex(it => it.id === id)
    const newItem = makeTodoItem()
    const next = [...items]
    next.splice(idx + 1, 0, newItem)
    focusItemRef.current = { id: newItem.id, caret: 'start' }
    setItems(next)
  }

  // Remove an item by id. The last item is replaced with a fresh blank
  // item rather than left empty — the todo block always renders at least
  // one row so the user has somewhere to keep typing.
  const removeItem = (id: string) => {
    const idx = items.findIndex(it => it.id === id)
    if (idx < 0) return false
    if (items.length <= 1) {
      const fresh = makeTodoItem()
      focusItemRef.current = { id: fresh.id, caret: 'start' }
      setItems([fresh])
      return true
    }
    const prev = items[idx - 1] ?? items[idx + 1]
    const next = items.filter(it => it.id !== id)
    if (prev) focusItemRef.current = { id: prev.id, caret: 'end' }
    setItems(next)
    return true
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} onFocus={onSelect}>
      {items.map((item) => (
        <div key={item.id} className="todo-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); updateItem(item.id, { checked: !item.checked }) }}
            style={{ background: 'transparent', border: 'none', padding: 0, marginTop: 2, cursor: 'pointer', color: 'var(--pg-text-secondary)' }}
            title={item.checked ? 'Uncheck' : 'Check'}
            tabIndex={-1}
          >
            {item.checked ? <CheckSquare size={16} color="var(--pg-primary)" /> : <Square size={16} />}
          </button>
          <TodoItemText
            item={item}
            registerRef={(el) => {
              if (el) itemRefs.current.set(item.id, el)
              else itemRefs.current.delete(item.id)
            }}
            onTextChange={(text) => updateItem(item.id, { text })}
            onEnter={() => insertAfter(item.id)}
            onBackspaceEmpty={() => removeItem(item.id)}
          />
          <button
            type="button"
            className="todo-row-remove"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              removeItem(item.id)
            }}
            title="Delete item"
            aria-label="Delete to-do item"
            tabIndex={-1}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  )
}

interface TodoItemTextProps {
  item: TodoItem
  registerRef: (el: HTMLDivElement | null) => void
  onTextChange: (text: string) => void
  onEnter: () => void
  onBackspaceEmpty: () => boolean
}

function TodoItemText({ item, registerRef, onTextChange, onEnter, onBackspaceEmpty }: TodoItemTextProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  // Sync external item.text → DOM only when it differs. Mirrors the same
  // caret-preserving pattern the main contenteditable uses elsewhere in
  // BlockNode so external updates (e.g. checkbox toggle) don't reset the
  // caret position mid-typing.
  useEffect(() => {
    const el = ref.current
    if (el && el.textContent !== item.text) {
      el.textContent = item.text
    }
  }, [item.text])

  return (
    <div
      ref={(el) => { ref.current = el; registerRef(el) }}
      contentEditable
      suppressContentEditableWarning
      data-placeholder="To-do"
      onInput={(e) => onTextChange((e.currentTarget as HTMLDivElement).innerText)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          onEnter()
          return
        }
        if (e.key === 'Backspace' && (e.currentTarget as HTMLDivElement).innerText === '') {
          if (onBackspaceEmpty()) e.preventDefault()
        }
      }}
      style={{
        outline: 'none',
        minHeight: '1.4em',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        flex: 1,
        fontSize: '1.0125rem',
        lineHeight: 1.5,
        color: 'var(--pg-text, #0f172a)',
        textDecoration: item.checked ? 'line-through' : 'none',
        opacity: item.checked ? 0.6 : 1,
      }}
    />
  )
}

// ─── Bullet / Numbered list editor ─────────────────────
// Mirrors the TodoListEditor pattern: a single block holds many items;
// Enter splits the current line, Backspace at the start of an empty
// line removes that item (last-remaining-item gets reset to a fresh
// empty line). Tab / Shift-Tab adjust indent. The × button is shown via
// the same `.todo-row-remove` styles.

interface ListBlockEditorProps {
  block: BulletListBlock | NumberedListBlock
  kind: 'bullet' | 'numbered'
  onChange: (next: Block) => void
  onSelect: () => void
}

function ListBlockEditor({ block, kind, onChange, onSelect }: ListBlockEditorProps) {
  const focusItemRef = useRef<{ id: string; caret: 'start' | 'end' } | null>(null)
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    const target = focusItemRef.current
    if (!target) return
    const el = itemRefs.current.get(target.id)
    if (el) {
      el.focus()
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(target.caret === 'start')
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(range)
    }
    focusItemRef.current = null
  })

  const items: ListItem[] = block.items && block.items.length > 0 ? block.items : [makeListItem()]
  const setItems = (next: ListItem[]) => onChange({ ...block, items: next } as Block)

  const updateItem = (id: string, patch: Partial<ListItem>) =>
    setItems(items.map(it => (it.id === id ? { ...it, ...patch } : it)))

  const insertAfter = (id: string) => {
    const idx = items.findIndex(it => it.id === id)
    if (idx < 0) return
    const newItem = makeListItem('', items[idx].indent ?? 0)
    const next = [...items]
    next.splice(idx + 1, 0, newItem)
    focusItemRef.current = { id: newItem.id, caret: 'start' }
    setItems(next)
  }

  const removeItem = (id: string) => {
    const idx = items.findIndex(it => it.id === id)
    if (idx < 0) return false
    if (items.length <= 1) {
      const fresh = makeListItem()
      focusItemRef.current = { id: fresh.id, caret: 'start' }
      setItems([fresh])
      return true
    }
    const prev = items[idx - 1] ?? items[idx + 1]
    const next = items.filter(it => it.id !== id)
    if (prev) focusItemRef.current = { id: prev.id, caret: 'end' }
    setItems(next)
    return true
  }

  const indentItem = (id: string, dir: 1 | -1) => {
    const item = items.find(it => it.id === id)
    if (!item) return
    const next = Math.max(0, Math.min(6, (item.indent ?? 0) + dir))
    if (next === (item.indent ?? 0)) return
    updateItem(id, { indent: next })
  }

  // Pre-compute the displayed marker for each item. For numbered lists we
  // restart numbering after every indent reset so a 3-level nested list
  // reads naturally (1, 1.1, 1.1.1, 2, 2.1, …). For bullet lists we
  // alternate the bullet glyph by depth for visual variety.
  const counters = computeNumberedCounters(items)
  const BULLETS = ['•', '◦', '▪']

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} onFocus={onSelect}>
      {items.map((item, idx) => {
        const indent = Math.max(0, Math.min(6, item.indent ?? 0))
        const marker = kind === 'bullet'
          ? BULLETS[indent % BULLETS.length]
          : `${counters[idx]}.`
        return (
          <div key={item.id} className="todo-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, paddingLeft: indent * 18 }}>
            <span style={{
              marginTop: kind === 'bullet' ? 4 : 2,
              color: 'var(--pg-text-secondary)',
              minWidth: kind === 'numbered' ? 18 : 0,
              flexShrink: 0,
              userSelect: 'none',
            }}>{marker}</span>
            <ListItemText
              item={item}
              registerRef={(el) => {
                if (el) itemRefs.current.set(item.id, el)
                else itemRefs.current.delete(item.id)
              }}
              onTextChange={(text) => updateItem(item.id, { text })}
              onEnter={() => insertAfter(item.id)}
              onBackspaceEmpty={() => removeItem(item.id)}
              onIndent={(dir) => indentItem(item.id, dir)}
            />
            <button
              type="button"
              className="todo-row-remove"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                removeItem(item.id)
              }}
              title="Delete item"
              aria-label="Delete list item"
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          </div>
        )
      })}
    </div>
  )
}

// For each item, return its displayed numbered-list index. Top-level
// items count 1, 2, 3 …; deeper items restart at 1 each time their
// parent number changes. The result matches Notion's behaviour and is
// easier to read than a flat global counter.
function computeNumberedCounters(items: ListItem[]): number[] {
  const out: number[] = []
  const stack: number[] = []
  for (const it of items) {
    const d = Math.max(0, Math.min(6, it.indent ?? 0))
    while (stack.length <= d) stack.push(0)
    stack.length = d + 1
    stack[d] += 1
    out.push(stack[d])
  }
  return out
}

interface ListItemTextProps {
  item: ListItem
  registerRef: (el: HTMLDivElement | null) => void
  onTextChange: (text: string) => void
  onEnter: () => void
  onBackspaceEmpty: () => boolean
  onIndent: (dir: 1 | -1) => void
}

function ListItemText({ item, registerRef, onTextChange, onEnter, onBackspaceEmpty, onIndent }: ListItemTextProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (el && el.textContent !== item.text) {
      el.textContent = item.text
    }
  }, [item.text])

  return (
    <div
      ref={(el) => { ref.current = el; registerRef(el) }}
      contentEditable
      suppressContentEditableWarning
      data-placeholder="List item"
      onInput={(e) => onTextChange((e.currentTarget as HTMLDivElement).innerText)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          onEnter()
          return
        }
        if (e.key === 'Tab') {
          e.preventDefault()
          onIndent(e.shiftKey ? -1 : 1)
          return
        }
        if (e.key === 'Backspace' && (e.currentTarget as HTMLDivElement).innerText === '') {
          if (onBackspaceEmpty()) e.preventDefault()
        }
      }}
      style={{
        outline: 'none',
        minHeight: '1.4em',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        flex: 1,
        fontSize: '1.0125rem',
        lineHeight: 1.5,
        color: 'var(--pg-text, #0f172a)',
      }}
    />
  )
}

function formatValue(v: unknown, fmt?: string): string {
  if (v == null || v === '') return ''
  if (typeof v === 'object') {
    try { return JSON.stringify(v) } catch { return String(v) }
  }
  const s = String(v)
  switch (fmt) {
    case 'number': {
      const n = Number(s)
      return isFinite(n) ? n.toLocaleString() : s
    }
    case 'currency': {
      const n = Number(s)
      return isFinite(n) ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n) : s
    }
    case 'percentage': {
      const n = Number(s)
      return isFinite(n) ? `${n.toLocaleString()}%` : s
    }
    case 'date':     { try { return new Date(s).toLocaleDateString() } catch { return s } }
    case 'datetime': { try { return new Date(s).toLocaleString() } catch { return s } }
    case 'time':     { try { return new Date(s).toLocaleTimeString() } catch { return s } }
    case 'json':     { try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s } }
    default:         return s
  }
}

/** Stand-in rows for a repeat on the canvas: `designCount` of them, spread
 *  over the board's lanes (its fixed lanes, else two sample ones) or across
 *  the plane, so the author sees the arrangement they are choosing. */
function placeholderRows(repeat: NonNullable<Block['repeat']>): Record<string, unknown>[] {
  const n = Math.max(1, Math.min(repeat.designCount ?? 3, 12))
  const layout = repeat.layout || {}
  const lanes = layout.lanes?.length ? layout.lanes.map((l) => l.value) : ['Lane A', 'Lane B']
  const cw = Number(layout.canvasWidth) || 1000
  const ch = Number(layout.canvasHeight) || 600
  return Array.from({ length: n }, (_, i) => {
    const row: Record<string, unknown> = { _txn_id: `placeholder-${i}` }
    if (layout.mode === 'board' && layout.groupBy) row[layout.groupBy] = lanes[i % lanes.length]
    if (layout.mode === 'positioned') {
      const cols = 4
      if (layout.x && !Number.isFinite(Number(layout.x))) row[layout.x] = (i % cols) * (cw / cols) + cw * 0.03
      if (layout.y && !Number.isFinite(Number(layout.y))) row[layout.y] = Math.floor(i / cols) * (ch / 3) + ch * 0.05
    }
    return row
  })
}
